import { diff } from "deep-object-diff";
import cloneDeep from "lodash/cloneDeep";
import get from "lodash/get";
import set from "lodash/set";
import has from "lodash/has";
// utils
import { getJWT } from "@utils/browser-helpers";
import { onlyUniqueItems } from "@utils/array-helpers";
import { searchKeyVal } from "@utils/search-helpers";
import {
  flattenNestedObj,
  returnPathTreeList,
  stringifyNestedKeyPaths,
  isEmptyObject,
  setSchemaDefaults,
  stringifyNestedObjectsAndArrays,
  isPlainObject,
} from "@utils/object-helpers";
import { getNowISO } from "@utils/time-helpers";
import {
  toKebabCase,
  camelToTitle,
  camelize,
  snakeToCamel,
  isSnakeCase,
  snakeToTitle,
} from "@utils/string-helpers";
// constants
import {
  SYF_CARD_ACCESS,
  REWARDS_MFE_ATHOS,
  SYPI_BRC,
} from "@constants/schema-management";

export const logDiffs = (
  initialConfig,
  currentConfig,
  featureName,
  arrayId = "client"
) => {
  let objectDiff = diff(initialConfig, currentConfig);
  const changedPaths =
    featureName === REWARDS_MFE_ATHOS
      ? stringifyNestedObjectsAndArrays(objectDiff)
      : stringifyNestedKeyPaths(objectDiff);
  const revisedPaths =
    featureName === SYF_CARD_ACCESS
      ? changedPaths.map((path) => {
          const key = path.split(".")[0];
          const id = get(currentConfig, key.concat(".", arrayId));
          const updatedPath = path.replace(key, id);
          return updatedPath;
        })
      : changedPaths;
  return revisedPaths.reduce((acc, cur, i) => {
    const diffValue = get(objectDiff, changedPaths[i]);
    acc[cur] = diffValue === undefined ? null : diffValue;
    return acc;
  }, {});
};

export const formatSMLoggedChanges = (loggedChanges) =>
  loggedChanges && Array.isArray(loggedChanges)
    ? `[${loggedChanges.join(", ")}]`
    : `\n ${Object.keys(loggedChanges).map(
        (rootKey) =>
          `${rootKey}: ${Object.keys(loggedChanges[rootKey])
            .map((subKeys) => `${subKeys}`)
            .join(", ")}`
      )}`;

/**
 *
 * @param {Array} scheduledChanges scheduled changes
 * @returns {Object} keysOccurrence
 * @description Takes in the current scheduled changes, counts and consolidates all keys into an object, returns object
 */
export const countOccurrenceOfKeyInScheduledChanges = (
  scheduledChanges = []
) => {
  const flatKeys = scheduledChanges
    ? scheduledChanges.reduce((acc, { schemaPath }) => {
        acc.push(...Object.keys(schemaPath.smS3 ? schemaPath.smS3 : {}));
        return acc;
      }, [])
    : [];
  const keysOccurrence = flatKeys.reduce((acc, keyPath) => {
    acc[keyPath] = (acc[keyPath] ? acc[keyPath] : 0) + 1;
    return acc;
  }, {});
  return keysOccurrence;
};

/**
 *
 * @param {Object} changeLog change log - unsaved changes or scheduled changes opened for editing
 * @param {Array} scheduledChanges scheduled changes
 * @description Reports if 3 or more scheduled changes are modifying a specific key
 */
export const currentKeysOccurrenceChangesReport = (
  changeLog,
  scheduledChanges
) => {
  const keyOccurrenceOnScheduledChanges =
    countOccurrenceOfKeyInScheduledChanges(scheduledChanges);
  const keyOccurrenceLimit = 3;
  const keysOverLimit = Object.entries(changeLog).filter(
    ([keyPath, value]) =>
      keyOccurrenceOnScheduledChanges[keyPath] &&
      keyOccurrenceOnScheduledChanges[keyPath] >= keyOccurrenceLimit
  );
  const scheduledChangesWithCommonKeys = Object.fromEntries(keysOverLimit);
  const successFailedKeysReport = Object.entries(
    scheduledChangesWithCommonKeys
  ).reduce((acc, [keyPath, value]) => {
    acc[keyPath] = { disable: true };
    return acc;
  }, {});
  return {
    scheduledChangesWithCommonKeys: scheduledChangesWithCommonKeys,
    successFailedKeysReport,
  };
};

export const mergeSmVersionChanges = (
  changeLog,
  smVersion,
  initialConfig,
  clients = [],
  logAdditionalDetails = {},
  allowNullValues = false
) => {
  const updatedChangeLog = Object.keys(changeLog);
  const summaryReview = [];
  updatedChangeLog.forEach((change) => {
    const sectionName = change.split(".")[0];
    const oldPath =
      smVersion === SYF_CARD_ACCESS && isNaN(sectionName)
        ? change.replace(sectionName, clients.indexOf(sectionName))
        : change;
    const modifiedSection =
      smVersion === SYF_CARD_ACCESS &&
      clients.length &&
      change &&
      change.length &&
      !isNaN(sectionName)
        ? clients[Number(sectionName)]
        : camelToTitle(sectionName);

    const newKeyValue = get(changeLog, change);
    const oldKeyValue = get(initialConfig, oldPath);
    const keyName =
      smVersion === SYF_CARD_ACCESS && !isNaN(sectionName)
        ? change.replace(sectionName, modifiedSection)
        : change;

    const updatedNewKeyValue = newKeyValue
      ? Array.isArray(newKeyValue) && newKeyValue.length > 0
        ? newKeyValue.join(", ")
        : isPlainObject(newKeyValue)
        ? Object.keys(newKeyValue).join(", ")
        : newKeyValue
      : typeof newKeyValue === "boolean" ||
        typeof newKeyValue === "number" ||
        (newKeyValue === null && allowNullValues)
      ? newKeyValue
      : "";
    const updatedOldKeyValue = oldKeyValue
      ? Array.isArray(oldKeyValue)
        ? oldKeyValue.join(", ")
        : typeof oldKeyValue === "object"
        ? Object.keys(oldKeyValue).join(", ")
        : oldKeyValue
      : oldKeyValue === undefined
      ? null
      : typeof oldKeyValue === "boolean" ||
        typeof oldKeyValue === "number" ||
        (oldKeyValue === null && allowNullValues)
      ? oldKeyValue
      : "";
    if (updatedNewKeyValue !== updatedOldKeyValue)
      summaryReview.push({
        section: modifiedSection,
        keyName: keyName,
        newKeyValue: updatedNewKeyValue,
        oldKeyValue: updatedOldKeyValue,
        ...(logAdditionalDetails && logAdditionalDetails[keyName]
          ? logAdditionalDetails[keyName]
          : {}),
      });
  });
  return summaryReview;
};

export const configSchedulerEnrichment = (
  storeContents,
  modifiedSections,
  importedConfig = {}
) => {
  const { config, schema, schemaConfigScheduler, schemaAllView } =
    storeContents;
  const {
    name,
    displayName,
    liveScheduleDate,
    oldScheduleDate,
    status,
    summaryView,
  } = schemaConfigScheduler.scheduleDraft;
  const { rawMasterSchema, activeReleaseVersion, selectedReleaseVersions } =
    schema;
  const { schemaDefaults } = schemaConfigScheduler;
  const { featureName } = schemaAllView;
  const schemaDefaultSummaryView =
    schemaDefaults?.[activeReleaseVersion] &&
    schemaDefaults?.[activeReleaseVersion].include
      ? schemaDefaults?.[activeReleaseVersion].preview
      : [];
  const { newConfig, release_version, newSummaryView, changeDict } =
    importedConfig || null;

  const clients =
    schemaAllView.featureName === SYF_CARD_ACCESS
      ? schemaAllView.schemaManagementConfig.map((feature) => feature.client)
      : [];

  return {
    release_version: release_version || activeReleaseVersion,
    configSchedules: {
      featureName,
      name: `${name}${
        selectedReleaseVersions.length > 1
          ? ` (${release_version || activeReleaseVersion})`
          : ""
      }`,
      displayName,
      liveScheduleDate,
      oldScheduleDate,
      status,
      modifiedDate: getNowISO(config.timeZone, "YYYY-MM-DDTHH:mm:ss"),
      sectionsModified:
        schemaAllView.featureName === SYF_CARD_ACCESS && clients.length
          ? Object.keys(modifiedSections).map((section) =>
              isNaN(section) ? section : clients[section]
            )
          : Object.keys(modifiedSections),
      summaryView: [
        ...(!isEmptyObject(importedConfig) ? newSummaryView : summaryView),
        ...(!release_version && schemaDefaultSummaryView.length
          ? schemaDefaultSummaryView
          : []),
      ],
      release_version: release_version || activeReleaseVersion,
      schemaPath: {
        smS3: !isEmptyObject(importedConfig)
          ? changeDict
          : schemaAllView.changeDict,
      },
      smConfigs: {
        smS3: !isEmptyObject(importedConfig)
          ? newConfig
          : schemaAllView.schemaManagementConfig,
        ...(rawMasterSchema &&
        !isEmptyObject(rawMasterSchema) &&
        isEmptyObject(importedConfig)
          ? { master_schema: rawMasterSchema, schemaUpdate: true }
          : {}),
      },
    },
  };
};

export const getModifiedSections = (configChanges) => {
  let modifiedSmSections = {};
  return configChanges && !isEmptyObject(configChanges)
    ? Object.keys(configChanges).reduce((acc, item) => {
        let section = item && item.length ? item.split(".")[0] : "";
        if (section) {
          acc[section] = section;
        }
        return acc;
      }, modifiedSmSections)
    : modifiedSmSections;
};

export const scheduleDraftReport = (scheduledDraftResults, featureName) => {
  let finalReport = {};
  for (let release_version in scheduledDraftResults) {
    let results = scheduledDraftResults[release_version];
    let { apiError, type, error_elements } = results;
    if (apiError && apiError.details) {
      finalReport = {
        type: "ALERT_DOCK",
        title: `Saving the ${release_version} draft was unsuccessful. `,
        message:
          "Please contact the application development team for assistance.",
      };
    } else {
      finalReport = {
        type: "TOAST_MESSAGE",
        message: `Saving the config was successful. `,
        redirect: `/schema-management-${toKebabCase(
          featureName
        )}-config-schedule`,
      };
    }
  }
  return finalReport;
};

export const getPageCategory = () => {
  const { schema_management_client } = getJWT();
  const pageContext = window.location.pathname.split(
    `management-${toKebabCase(schema_management_client)}-`
  )[1];
  const subFeatureName = camelize(pageContext || schema_management_client);
  return subFeatureName;
};

export const getUiSchemaTypedArray = (schema, uiSchema = {}) => {
  const isArray = schema.hasOwnProperty("items");
  const { properties } = isArray ? schema.items : schema;
  if (properties && !isEmptyObject(properties)) {
    Object.keys(properties).forEach((key) => {
      if (isArray) {
        uiSchema["items"] = uiSchema.hasOwnProperty("items")
          ? uiSchema["items"]
          : {};
        uiSchema["items"][key] = {
          ...(properties[key].hasOwnProperty("properties") ||
          properties[key].hasOwnProperty("items")
            ? getUiSchemaTypedArray(properties[key])
            : {}),
        };
      } else if (isArray) {
        uiSchema["items"] = {};
      } else {
        uiSchema[key] = {
          ...(properties[key].hasOwnProperty("properties") ||
          properties[key].hasOwnProperty("items")
            ? getUiSchemaTypedArray(properties[key])
            : {}),
        };
      }
    });
    return uiSchema;
  }
};

export function getUiSchemaHelper(objToIter, definitions, overwriteUi = {}) {
  const aPaletteVals = ["color_hex", "palette", "text_palette"];
  return Object.keys(objToIter).reduce((acc, cur) => {
    if (objToIter[cur].hasOwnProperty("properties")) {
      acc[cur] = getUiSchemaHelper(objToIter[cur].properties, definitions);
    } else if (objToIter[cur].hasOwnProperty("$ref") && definitions) {
      let def = objToIter[cur].$ref.split("/");
      def = def[def.length - 1];
      overwriteUi = aPaletteVals.includes(def) ? { "ui:widget": "color" } : {};
      acc[cur] = definitions[def].hasOwnProperty("properties")
        ? getUiSchemaHelper(
            definitions[def].properties,
            definitions,
            overwriteUi
          )
        : isEmptyObject(overwriteUi)
        ? { "ui:emptyValue": "" }
        : overwriteUi;
    } else {
      acc[cur] = {};
    }
    return acc;
  }, {});
}

export const getUiSchemaTypedObject = (schema, uiSchema) => {
  let definitions = schema?.definitions || schema?.$defs;
  let schemaSection = schema.properties;
  uiSchema = { ...getUiSchemaHelper(schemaSection, definitions), ...uiSchema };
  return uiSchema;
};

export const getUiSchema = (schema, uiSchema = {}) => {
  const isArray = schema.hasOwnProperty("items");
  if (isArray) {
    return getUiSchemaTypedArray(schema, uiSchema);
  } else {
    return getUiSchemaTypedObject(schema, uiSchema);
  }
};

export const getConfigWithUpdatedSchemaDefaults = (
  pathConfigChanges,
  smClientConfig,
  masterConfigTemplate
) => {
  const configWithUpdatedSchemaDefaults = pathConfigChanges.reduce(
    (accSypiConfig, path) => {
      let parentPath = "";
      let keys = path.split(".");
      const arrayElement = keys.findIndex((el) => !isNaN(el));
      const isArrayElement = arrayElement !== -1;
      if (isArrayElement) {
        keys.splice(arrayElement);
        parentPath = keys.join(".");
      }
      if (
        (has(accSypiConfig, path) && has(smClientConfig, path)) ||
        (isArrayElement &&
          has(smClientConfig, path) &&
          has(accSypiConfig, parentPath) &&
          Array.isArray(get(smClientConfig, parentPath)))
      ) {
        const savedValue = get(smClientConfig, path);
        set(accSypiConfig, path, savedValue);
      }
      return accSypiConfig;
    },
    masterConfigTemplate
  );
  return configWithUpdatedSchemaDefaults;
};

export const getKeyDefinitions = (schema, keyDefinitions = []) => {
  if (!isEmptyObject(schema) && !keyDefinitions.length) {
    let definitions = schema?.definitions || schema?.$defs;
    let keysToFilter = Object.keys(definitions).reduce((acc, cur) => {
      if (definitions[cur].hasOwnProperty("required")) {
        return onlyUniqueItems([...acc, ...definitions[cur].required]);
      } else return acc;
    }, []);
    return keysToFilter;
  }
};

export const getSearchedSchemaTypedObject = (
  schema,
  formData,
  parentCategory,
  searchValue,
  keyDefinitions = []
) => {
  let formatted = flattenNestedObj(formData[parentCategory]);
  let matchedPaths = searchKeyVal(formatted, searchValue);
  let consolidatedLevels = matchedPaths.map((matchedVal) =>
    keyDefinitions.find((subKey) => matchedVal.includes(subKey))
      ? matchedVal.substring(0, matchedVal.lastIndexOf("."))
      : matchedVal
  );
  let parentPaths = [...new Set(consolidatedLevels)];
  consolidatedLevels = parentPaths.map((path) =>
    path.split(".").join(".properties.")
  );
  let subSchema = schema.properties[parentCategory].properties;
  let searchSubSchema = {
    ...schema,
    required: [parentCategory],
    minProperties: 0,
    properties: { [parentCategory]: { type: "object", properties: {} } },
  };

  if (matchedPaths.length) {
    for (let i in consolidatedLevels) {
      let pathList = returnPathTreeList(parentPaths[i]);
      let target = get(subSchema, consolidatedLevels[i]);
      for (let j in pathList) {
        let realPathList = (subProp) =>
          has(subSchema, `${pathList[j]}.${subProp}`)
            ? `${pathList[j]}.${subProp}`
            : `${pathList[j].split(".").join(".properties.")}.${subProp}`;
        let type = get(subSchema, realPathList("type"));
        if (type) {
          set(
            searchSubSchema,
            `properties.${parentCategory}.properties.${realPathList("type")}`,
            type
          );
          if (type === "array") {
            let itemType = get(subSchema, realPathList("items"));
            set(
              searchSubSchema,
              `properties.${parentCategory}.properties.${realPathList(
                "items"
              )}`,
              itemType
            );
          }
        }
      }
      set(
        searchSubSchema,
        `properties.${parentCategory}.properties.${consolidatedLevels[i]}`,
        target
      );
    }
  }
  searchSubSchema = schemaFormatter(searchSubSchema);
  return searchSubSchema;
};

export const schemaFormatter = (schemaToFormat) => ({
  ...schemaToFormat,
  /**
   * @TODO Lets think if we need to add unsaved properties to the schema
   */
  ...(schemaToFormat?.hasOwnProperty("$defs")
    ? {
        $defs: addMinLength(addTitles(schemaToFormat?.$defs)),
      }
    : {
        definitions: addMinLength(
          addTitles(schemaToFormat?.definitions || schemaToFormat?.items)
        ),
      }),
  properties: addTitles(
    schemaToFormat?.properties || schemaToFormat?.items?.properties
  ),
});
const addTitles = (obj, accObj = {}) => {
  accObj = Object.keys(obj).reduce((acc, cur) => {
    acc[cur] = {
      ...obj[cur],
      title: isSnakeCase(cur)
        ? snakeToTitle(cur)
        : camelToTitle(snakeToCamel(cur)),
    };
    if (obj[cur] && obj[cur].hasOwnProperty("properties")) {
      let currentProp = obj[cur].properties;
      acc[cur].properties = { ...addTitles(currentProp, acc) };
    } else if (
      obj[cur] &&
      obj[cur].hasOwnProperty("items") &&
      obj[cur]?.items.hasOwnProperty("properties")
    ) {
      let currentProp = obj[cur].items.properties;
      acc[cur].items.properties = { ...addTitles(currentProp, acc) };
    }
    return acc;
  }, {});
  return accObj;
};

const addMinLength = (obj, accObj = {}) => {
  accObj = Object.keys(obj).reduce((acc, cur) => {
    acc[cur] = {
      ...obj[cur],
      ...(obj[cur].hasOwnProperty("type") &&
      (obj[cur].type === "string" ||
        (Array.isArray(obj[cur].type) && !obj[cur].type.includes("null")))
        ? { minLength: 1 }
        : {}),
    };
    if (obj[cur].hasOwnProperty("properties")) {
      let currentProp = obj[cur].properties;
      acc[cur].properties = { ...addMinLength(currentProp, acc) };
    }
    return acc;
  }, {});
  return accObj;
};

export const convertPatternPropertiesSchemaToPropertiesSchema = (
  schema,
  clientsNames
) => {
  let newSchema = cloneDeep(schema);
  if (newSchema.patternProperties) {
    const patternSchema = schema.patternProperties["^[A-Za-z_][A-Za-z0-9_]*$"];
    newSchema = removePatternProperty(newSchema);
    newSchema.properties = {};
    clientsNames?.forEach((clientsName) => {
      newSchema.properties[clientsName] = cloneDeep(patternSchema);
    });
    return newSchema;
  }
};
const removePatternProperty = ({ patternProperties, ...otherProps }) =>
  otherProps;

export const prepareArrayPaths = (
  changes,
  initialConfig,
  itemId = "client"
) => {
  const existingPaths = stringifyNestedKeyPaths(initialConfig);
  const existingIds = existingPaths
    .filter((path) => path.split(".").pop() === itemId)
    .reduce((dict, idPath) => {
      const index = idPath.split(".")[0];
      const id = get(initialConfig, idPath);
      set(dict, id, index);
      return dict;
    }, {});

  let newClients = {};
  let newClientInit = initialConfig.length;
  const paths = Object.keys(changes).reduce((acc, path) => {
    const id = path.split(".")[0];
    const index = existingIds[id] || newClients[id];
    if (!isNaN(id)) {
      acc.push(path);
    } else if (index === undefined) {
      acc.push(path.replace(id, newClientInit));
      set(newClients, id, newClientInit);
      newClientInit++;
    } else {
      acc.push(path.replace(id, index));
    }
    return acc;
  }, []);
  return paths;
};

export const generateArrayBlankConfig = (
  schemaParam,
  defaultOverride = null
) => {
  const schemaTopLevel = schemaParam.items.properties;
  return [
    Object.keys(schemaTopLevel).reduce((acc, cur) => {
      if (schemaTopLevel.hasOwnProperty(cur)) {
        acc[cur] = schemaTopLevel[cur].properties
          ? setSchemaDefaults(
              schemaTopLevel[cur].properties,
              defaultOverride
                ? defaultOverride
                : cur !== "features"
                ? ""
                : false,
              schemaParam.definitions,
              defaultOverride
            )
          : "";
      }
      return acc;
    }, {}),
  ];
};

export const getCustomizedUiSchema = (
  defaultUiSchema,
  clientSchemaManagement
) => {
  let clonedUiSchema = cloneDeep(defaultUiSchema);

  switch (clientSchemaManagement) {
    case SYF_CARD_ACCESS:
      clonedUiSchema["ui:options"] = {
        orderable: false,
        label: false,
      };
      clonedUiSchema.items = {
        "ui:label": false,
        ...clonedUiSchema.items,
        client: {
          ...clonedUiSchema.items.client,
          "ui:label": false,
          "ui:emptyValue": "",
        },
        features: {
          ...clonedUiSchema.items.features,
          "ui:label": false,
        },
      };
      break;
    case REWARDS_MFE_ATHOS:
      clonedUiSchema["ui:options"] = {
        expandable: false,
      };
      break;
    case SYPI_BRC:
      clonedUiSchema.content = {
        apply: {
          applicationResults: {
            approval: {
              promoTiles: {
                items: {
                  "ui:classname": "removable",
                  active: {
                    "ui:label": false,
                    "ui:widget": "hidden",
                  },
                },
              },
            },
          },
        },
      };
      break;
    default:
      break;
  }
  return clonedUiSchema;
};
